import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { Grid } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { Utils } from '@pbt/pbt-ui-components'

import {
  AgentIngressButton,
  AgentIngressPromptType,
} from '~/components/agent/ingress'
import PuiButtonGroup from '~/components/common/buttons/PuiButtonGroup'
import SwapProductSuccessToast from '~/components/common/toasts/SwapProductSuccessToast/SwapProductSuccessToast'
import { AlertBanner } from '~/components/elements/AlertBanner/AlertBanner'
import FeatureToggle from '~/constants/featureToggle'
import { LandingType } from '~/constants/landingConstants'
import SnapshotsAliasTypes from '~/constants/SnapshotsAliasTypes'
import type { TimelineEntryType } from '~/constants/timelineConstants'
import { useAgentIngress } from '~/hooks/useAgentIngress'
import i18n from '~/locales/i18n'
import { getWidgetData } from '~/store/duck/landing'
import {
  getAIAgentProcessStatuses,
  getAIAgentProcessTypes,
  getDiagnosisStatus,
  getFeatureToggle,
} from '~/store/reducers/constants'
import { getUploadedDocuments } from '~/store/reducers/medicalHistory'
import { ProblemStates } from '~/types'

import RecordsComponent from './records/RecordsComponent'
import SnapshotsComponent from './snapshots/SnapshotsComponent'

const useStyles = makeStyles(
  (theme) => ({
    switchComponentContainer: {
      backgroundColor: theme.colors.tableBackground,
      boxShadow: '0 2px 4px 0 rgba(0,0,0,0.1)',
    },
  }),
  { name: 'SnapshotsAndRecordsComponent' },
)

const ActiveMode = {
  SNAPSHOTS: 'snapshots',
  RECORDS: 'records',
}

const SnapshotsAndRecordsStates = [
  {
    label: i18n.t('Clients:SNAPSHOTS_AND_RECORDS.PATIENT_SNAPSHOTS_LABEL'),
    component: SnapshotsComponent,
  },
  {
    label: i18n.t('Clients:SNAPSHOTS_AND_RECORDS.PATIENT_RECORDS_LABEL'),
    component: RecordsComponent,
  },
]

const ModeRegex =
  /^\/client\/([A-Za-z0-9]+)\/patient\/([A-Za-z0-9]+)\/?([A-Za-z0-9]+)?/
const getModeFromUrl = (pathname: string) => pathname.match(ModeRegex)?.[3]

interface SnapshotsAndRecordsComponentProps {
  clientId: string
  patientId: string
}

const SnapshotsAndRecordsComponent = ({
  clientId,
  patientId,
}: SnapshotsAndRecordsComponentProps) => {
  const classes = useStyles()
  const { t } = useTranslation(['MedicalHistory', 'Plurals'])

  const location = useLocation()
  const navigate = useNavigate()
  const [showSwapProductSuccess, setShowSwapProductSuccess] = useState(false)
  const [applyFilters, setApplyFilters] = useState<TimelineEntryType[]>([])
  const activeTimelineType =
    getModeFromUrl(location.pathname) || ActiveMode.SNAPSHOTS

  // Handle swap product success toast and timeline filter trigger
  useEffect(() => {
    const state = location.state as {
      applyFilters?: TimelineEntryType[]
      showSwapProductSuccess?: boolean
    }
    if (state?.showSwapProductSuccess) {
      setShowSwapProductSuccess(true)
      setApplyFilters(state.applyFilters || [])
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, location.pathname, navigate])
  const isPmrAiSummarizationEnabled = useSelector(
    getFeatureToggle(FeatureToggle.PMR_AI_SUMMARIZATION),
  )

  const diagnoses = useSelector(
    getWidgetData(
      LandingType.CLIENT_AND_PATIENT_SNAPSHOTS,
      SnapshotsAliasTypes.Problems,
    ),
  )
  const DiagnosisStatus = useSelector(getDiagnosisStatus)
  const AddedByAiDiagnosisStatusId = Utils.findConstantIdByName(
    ProblemStates.ADDED_BY_AI,
    DiagnosisStatus,
  )

  const pmrItemsForReview = diagnoses.filter(
    (diagnosis) => diagnosis.statusId === AddedByAiDiagnosisStatusId,
  )

  const uploadedDocuments = useSelector(getUploadedDocuments)
  const processStatusIds = useSelector(getAIAgentProcessStatuses)
  const processTypeIds = useSelector(getAIAgentProcessTypes)
  const ProblemProcessingTypeId = Utils.findConstantIdByName(
    'PMR Diagnosis Extraction',
    processTypeIds,
  )
  const ProcessingStatusTypeId = Utils.findConstantIdByName(
    'Processing',
    processStatusIds,
  )
  const CompletedStatusTypeId = Utils.findConstantIdByName(
    'Completed',
    processStatusIds,
  )

  const processingDocuments = uploadedDocuments.filter(
    (doc) =>
      doc.processStatuses.find(
        (ps) => ps.processTypeId === ProblemProcessingTypeId,
      )?.processStatusId === ProcessingStatusTypeId,
  )
  const readyForReviewDocuments = uploadedDocuments.filter(
    (doc) =>
      doc.processStatuses.find(
        (ps) => ps.processTypeId === ProblemProcessingTypeId,
      )?.processStatusId === CompletedStatusTypeId,
  )

  const setActiveMode = (mode: string, replace: boolean) => {
    navigate(`/client/${clientId}/patient/${patientId}/${mode}`, {
      replace,
    })
  }

  const activeState =
    activeTimelineType === ActiveMode.RECORDS
      ? SnapshotsAndRecordsStates[1]
      : SnapshotsAndRecordsStates[0]

  const Component = activeState.component

  const { handleIngress } = useAgentIngress({
    patientId,
    clientId,
  })

  return (
    <Grid container direction="column">
      <Grid
        container
        alignItems="center"
        className={classes.switchComponentContainer}
        px={2}
        py={1}
      >
        <PuiButtonGroup
          items={SnapshotsAndRecordsStates}
          selectedItem={activeState}
          onItemSelected={() =>
            // For new context flow we don't want to replace the URL, as we will have back button
            setActiveMode(
              activeTimelineType === ActiveMode.SNAPSHOTS
                ? ActiveMode.RECORDS
                : ActiveMode.SNAPSHOTS,
              false,
            )
          }
        />
        <Grid item ml={2}>
          <AgentIngressButton
            text={t('Agent:SUMMARIZE_HISTORY')}
            onClick={() =>
              handleIngress(AgentIngressPromptType.MEDICAL_HISTORY)
            }
          />
        </Grid>
      </Grid>
      {showSwapProductSuccess && (
        <Grid mt={1} px={2}>
          <SwapProductSuccessToast
            open={showSwapProductSuccess}
            onClose={() => setShowSwapProductSuccess(false)}
          />
        </Grid>
      )}
      {isPmrAiSummarizationEnabled &&
        readyForReviewDocuments.map((document) => {
          const itemsCount = pmrItemsForReview.filter(
            (item) =>
              item.previousMedicalRecordId ===
              document.referenceEntity.entityId,
          ).length

          if (itemsCount === 0) {
            return null
          }

          return (
            <Grid key={document.referenceEntity.entityId} mt={1} px={2}>
              <AlertBanner
                ContainerProps={{ width: '100%' }}
                actions={{
                  linkText: t('Plurals:Z_ICU_WORKAROUND.REVIEW_ITEMS', {
                    numRecords: itemsCount,
                  }),
                  onClick: () =>
                    navigate(
                      `/client/${clientId}/patient/${patientId}/previous-medical-records/${document.referenceEntity.entityId}`,
                    ),
                }}
                contentMargin={0.5}
                title={t(
                  'MedicalHistory:PMR_AI_SUMMARIZATION.READY_FOR_REVIEW',
                  {
                    documentName: document.referenceEntity.fileName,
                  },
                )}
                variant="good"
              />
            </Grid>
          )
        })}
      {isPmrAiSummarizationEnabled &&
        processingDocuments.map((document) => (
          <Grid key={document.referenceEntity.entityId} mt={1} px={2}>
            <AlertBanner
              ContainerProps={{ width: '100%' }}
              title={t(
                'MedicalHistory:PMR_AI_SUMMARIZATION.PROCESSING_DOCUMENT',
                {
                  documentName: document.referenceEntity.fileName,
                },
              )}
              variant="loading"
            />
          </Grid>
        ))}
      {activeTimelineType && (
        <Component
          applyFilters={applyFilters}
          clientId={clientId}
          patientId={patientId}
        />
      )}
    </Grid>
  )
}

export default SnapshotsAndRecordsComponent
